/** Organization forms and UI state; HTTP validators are generated from OpenAPI. */

import { z } from "zod";
import { organizationSchema, organizationsListResponseSchema } from "@/generated/organization-contract";
export { organizationSchema, organizationsListResponseSchema };

/** Population archetypes — the org's sign-in door vocabulary. The
 *  display copy lives with the tenant-level editor
 *  (identity-trust-settings/schemas.ts, ARCHETYPE_LABELS) and is
 *  reused, not duplicated. */
export const orgArchetypeSchema = z.enum([
  "workforce_iam",
  "standalone_iam",
  "consumer_ciam",
]);
export type OrgArchetype = z.infer<typeof orgArchetypeSchema>;

export const orgStatusSchema = z.enum(["active", "suspended", "deactivated"]);
export type OrgStatus = z.infer<typeof orgStatusSchema>;

export type Organization = z.infer<typeof organizationSchema>;
export type OrganizationsListResponse = z.infer<typeof organizationsListResponseSchema>;

/** URL-backed list state (validated by the route's search schema). */
export const organizationsListSearchSchema = z.object({
  status: orgStatusSchema.optional().catch(undefined),
  cursor: z.string().optional().catch(undefined),
});
export type OrganizationsListSearch = z.infer<
  typeof organizationsListSearchSchema
>;

export interface CreateOrganizationInput {
  name: string;
  slug: string;
  display_name?: string;
  contact_name?: string;
  contact_email?: string;
  /** Customer tenants only — the backend refuses it elsewhere. */
  max_users?: number;
}

export interface UpdateOrganizationInput {
  name?: string;
  display_name?: string;
  contact_name?: string;
  contact_email?: string;
  /** null clears the cap; undefined leaves it untouched. */
  max_users?: number | null;
}

/** The organization page's tabs; the URL carries the choice. */
export const organizationTabSchema = z.enum(["overview", "members", "requests", "sign-in", "provisioning", "delegated", "settings"]);
export type OrganizationTab = z.infer<typeof organizationTabSchema>;

// ── Delegated access (F79) ──────────────────────────────────────────────────
//
// An organization grant gives one of the tenant's admins an organization-scope
// role over this organization: the confined organization console. Wire shape
// from backend/pkg/api/admin/orggrant.go.

export const orgGrantRoleSchema = z.enum(["org_admin", "org_operator"]);
export type OrgGrantRole = z.infer<typeof orgGrantRoleSchema>;

export const ORG_GRANT_ROLE_LABEL: Record<OrgGrantRole, string> = {
  org_admin: "Organization admin",
  org_operator: "Organization operator",
};

export const orgGrantSchema = z.object({
  id: z.string(),
  admin_user_id: z.string(),
  tenant_id: z.string(),
  organization_id: z.string(),
  role: orgGrantRoleSchema,
  granted_by: z.string().optional(),
  granted_at: z.string(),
  expires_at: z.string().optional(),
  revoked_at: z.string().optional(),
  revoked_by: z.string().optional(),
});
export type OrgGrant = z.infer<typeof orgGrantSchema>;
export const orgGrantListSchema = z.object({ items: z.array(orgGrantSchema) });

export interface CreateOrgGrantInput {
  admin_user_id: string;
  role: OrgGrantRole;
  /** RFC 3339; the server defaults and caps the window. */
  expires_at?: string;
}
